import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Repeat, X } from 'lucide-react';
import { supabase } from '../../supabase';
import { listStockVariantOptions, getVariantAttributesLabel } from '../../utils/variantStock';

/**
 * Cambio de talle/color de una prenda vendida, al MISMO precio.
 * Toda la operación (stock de ambas variantes, kardex y registro) la hace
 * fn_cambio_talle en una transacción; si el precio difiere, la base lo rechaza
 * y hay que registrar devolución + venta nueva.
 */
export default function CambioTalleModal({ saleItem, sites = [], defaultSiteId = '', onClose, onDone }) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [siteId, setSiteId] = useState(defaultSiteId || '');
  const [variantId, setVariantId] = useState('');
  const [cantidad, setCantidad] = useState(1);
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('id, name, sell_price, stock_tracking, product_variants(id, talle, talle_orden, color_id, activo, archivado_at, precio_override, colores(id, nombre, hex)), stock_variantes(variant_id, site_id, quantity)')
        .eq('id', saleItem.product_id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.error(error);
        toast.error('No se pudieron cargar los talles de la prenda.');
      }
      setProduct(data || null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [saleItem.product_id]);

  const vendida = useMemo(
    () => (product?.product_variants || []).find((v) => v.id === saleItem.variant_id) || null,
    [product, saleItem.variant_id],
  );
  const precioDe = (variant) => {
    const override = variant?.precio_override;
    return override !== null && override !== undefined && override !== '' ? Number(override) : Number(product?.sell_price || 0);
  };
  const opciones = useMemo(
    () => listStockVariantOptions(product, siteId || null).filter((o) => o.id !== saleItem.variant_id),
    [product, siteId, saleItem.variant_id],
  );
  const elegida = opciones.find((o) => o.id === variantId) || null;
  const mismoPrecio = elegida && vendida ? precioDe(elegida.variant) === precioDe(vendida) : true;

  const confirmar = async () => {
    if (!siteId) return toast.error('Elegí la sucursal donde se hace el cambio.');
    if (!elegida) return toast.error('Elegí el talle/color que se lleva.');
    const qty = Number(cantidad);
    if (!Number.isFinite(qty) || qty <= 0) return toast.error('Cantidad inválida.');
    setSaving(true);
    try {
      const { error } = await supabase.rpc('fn_cambio_talle', {
        p_sale_item: saleItem.id,
        p_variant_nueva: elegida.id,
        p_cantidad: qty,
        p_site: siteId,
        p_motivo: motivo.trim() || null,
      });
      if (error) throw error;
      toast.success(`Cambio registrado: ${getVariantAttributesLabel(vendida) || 'talle vendido'} → ${elegida.label}`);
      onDone?.();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'No se pudo registrar el cambio.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={saving ? undefined : onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-[#0B2A36] px-4 py-3 flex items-center justify-between">
          <span className="flex items-center gap-2 text-[11px] font-black text-white uppercase tracking-widest">
            <Repeat size={16} className="text-[#3FF2AA]" /> Cambio de talle / color
          </span>
          <button type="button" onClick={onClose} disabled={saving} className="text-white/70 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-sm font-black text-slate-800 uppercase">{saleItem.product_name_snapshot || product?.name || 'Prenda'}</p>
            <p className="text-[11px] text-slate-500">
              Devuelve: <b>{getVariantAttributesLabel(vendida) || '—'}</b> · mismo precio, sin movimiento de dinero.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Sucursal</span>
              <select value={siteId} onChange={(e) => { setSiteId(e.target.value); setVariantId(''); }}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold">
                <option value="">Seleccionar…</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Cantidad</span>
              <input type="number" min="1" max={saleItem.quantity} value={cantidad} onChange={(e) => setCantidad(e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs font-black text-center" />
            </label>
          </div>

          <div className="space-y-1">
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Se lleva</span>
            {loading ? <p className="text-xs text-slate-400">Cargando talles…</p> : (
              <div className="flex flex-wrap gap-1.5">
                {opciones.length === 0 && <p className="text-xs text-rose-600">No hay otros talles/colores activos.</p>}
                {opciones.map((o) => {
                  const sinStock = siteId && o.stock <= 0;
                  const otroPrecio = vendida && precioDe(o.variant) !== precioDe(vendida);
                  return (
                    <button key={o.id} type="button" onClick={() => setVariantId(o.id)}
                      title={otroPrecio ? 'Precio distinto: se registra como devolución + venta' : undefined}
                      className={`px-2.5 py-1.5 rounded-lg border text-[10px] font-black uppercase transition-all ${
                        variantId === o.id ? 'bg-[#0B2A36] text-white border-[#0B2A36]'
                          : sinStock || otroPrecio ? 'bg-white text-slate-300 border-slate-100'
                            : 'bg-white text-slate-700 border-slate-200 hover:border-[#51A7FD]'}`}>
                      {o.label}{siteId ? <span className="ml-1.5 opacity-70">{o.stock}</span> : null}
                    </button>
                  );
                })}
              </div>
            )}
            {elegida && !mismoPrecio && (
              <p className="text-[11px] font-bold text-amber-700">Ese talle tiene otro precio: registralo como devolución y venta nueva.</p>
            )}
          </div>

          <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo (opcional): le quedaba chica, otro color…"
            className="w-full px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs" />

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} disabled={saving}
              className="px-4 py-2 rounded-xl border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-600">Cancelar</button>
            <button type="button" onClick={confirmar} disabled={saving || !elegida || !mismoPrecio || !siteId}
              className="px-4 py-2 rounded-xl bg-[#51A7FD] text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-40">
              {saving ? 'Registrando…' : 'Confirmar cambio'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
